import React, { useEffect, useState } from 'react';
import {
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  LinearProgress,
} from '@mui/material';
import DialogWindow from '@/common/components/_misc/DialogWindow';
import { FileSyncStatus } from '@/common/models/enums';
import { CloudUpload as PushIcon } from '@mui/icons-material';
import {
  getFileSyncStatusIcon,
  getFileSyncStatusColor,
} from '@/common/utils/statusUtils';

export interface FileStatus {
  fileName: string;
  status: FileSyncStatus;
  isLocal: boolean;
}

export interface CommitDialogProps {
  open: boolean;
  onClose: () => void;
  onCommit: (commitMessage: string) => Promise<void>;
  fileStatuses: FileStatus[];
  isCommitting: boolean;
  isScanning?: boolean;
  onRetry?: () => void;
  error?: string;
  initialCommitMessage?: string;
}

const CommitDialog: React.FC<CommitDialogProps> = ({
  open,
  onClose,
  onCommit,
  fileStatuses,
  isCommitting,
  isScanning = false,
  onRetry,
  error,
  initialCommitMessage = '',
}) => {
  const [commitMessage, setCommitMessage] = useState('');
  useEffect(() => {
    if (open) setCommitMessage(initialCommitMessage);
  }, [open, initialCommitMessage]);

  const handleCommit = async () => {
    if (!commitMessage.trim()) {
      return;
    }

    await onCommit(commitMessage.trim());
  };

  const handleClose = () => {
    if (!isCommitting) {
      setCommitMessage('');
      onClose();
    }
  };

  // Filter files that have changes
  const changedFiles = fileStatuses.filter(
    (file) =>
      file.status !== FileSyncStatus.UP_TO_DATE &&
      file.status !== FileSyncStatus.UNKNOWN
  );
  const needsReview = fileStatuses.some(
    (file) =>
      file.status === FileSyncStatus.UNKNOWN ||
      file.status === FileSyncStatus.BEHIND
  );

  return (
    <DialogWindow
      open={open}
      onClose={handleClose}
      title="Create New Version"
      maxWidth="md"
      fullWidth
    >
      <DialogContent>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              onRetry && (
                <Button
                  color="inherit"
                  onClick={onRetry}
                  disabled={isScanning || isCommitting}
                >
                  Retry scan
                </Button>
              )
            }
          >
            {error}
          </Alert>
        )}

        {isScanning ? (
          <Box role="status" sx={{ py: 3 }}>
            <Typography sx={{ mb: 2 }}>Scanning changes…</Typography>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary">
              Checking your local files against the latest cloud version.
              Unchanged files reuse cached hashes.
            </Typography>
          </Box>
        ) : error ? null : changedFiles.length === 0 && !needsReview ? (
          <Alert severity="info">
            No changes detected. All files are up to date.
          </Alert>
        ) : (
          <>
            {needsReview && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Some files are unreadable or behind the cloud version. Review
                Sync before creating a version.
              </Alert>
            )}
            <Typography variant="h6" gutterBottom>
              Changes in this version ({changedFiles.length} files):
            </Typography>

            <List dense sx={{ mb: 2, maxHeight: 200, overflow: 'auto' }}>
              {changedFiles.map((file) => (
                <ListItem key={file.fileName}>
                  <ListItemIcon>
                    {getFileSyncStatusIcon(file.status)}
                  </ListItemIcon>
                  <ListItemText
                    slotProps={{ secondary: { component: 'div' } }}
                    primary={file.fileName}
                    secondary={
                      <Chip
                        label={file.status}
                        size="small"
                        color={getFileSyncStatusColor(file.status)}
                        variant="outlined"
                      />
                    }
                  />
                </ListItem>
              ))}
            </List>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Version Notes"
              placeholder="Describe this version..."
              value={commitMessage}
              onChange={(e) => setCommitMessage(e.target.value)}
              disabled={isCommitting}
              helperText={`${commitMessage.length}/200 characters`}
              inputProps={{ maxLength: 200 }}
              sx={{ mb: 2 }}
            />

            {isCommitting && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Creating new version...
                </Typography>
                <LinearProgress />
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isCommitting}>
          Cancel
        </Button>
        <Button
          onClick={handleCommit}
          variant="contained"
          startIcon={<PushIcon />}
          disabled={
            isCommitting ||
            isScanning ||
            !!error ||
            needsReview ||
            !commitMessage.trim() ||
            changedFiles.length === 0
          }
        >
          {isCommitting ? 'Creating...' : 'Create Version'}
        </Button>
      </DialogActions>
    </DialogWindow>
  );
};

export default CommitDialog;
